<template>
  <UsageSummaryCard
    title="Record Export Summary"
    label="Records exported"
    :used="recordsExported"
    :limit="exportBaseLimit ?? null"
    :percent-text="percentOfBase"
    :width="limitUsedWidth"
    :color="statusColor"
  >
    <template #sub-line>
      <span v-if="hasExportExcess" data-testid="export-excess-note"
        >+{{ exportExcess.toLocaleString() }} authorized under your
        agreement</span
      >
      <span v-else
        >Better understand record exports & see a full breakdown of usage</span
      >
    </template>
    <template #action>
      <BittsButton
        variant="ghost"
        size="x-small"
        text="See usage"
        data-testid="record-export-summary-see-usage"
        @click="isRecordExportModalVisible = !isRecordExportModalVisible"
      />
    </template>
    <template #callout>
      <UsageLimitCallout
        v-if="calloutContent"
        class="mt-16"
        :content="calloutContent"
        @contact-us="onContactUs"
      />
    </template>
  </UsageSummaryCard>
</template>

<script setup lang="ts">
import { BittsButton } from '@crossbeam/bitts';
import { EVENT_SITES, PLAN_AND_BILLING_CTAS } from '@crossbeam/itly';

import { computed } from 'vue';

import type { UsageStatusColor } from '@/composables/credits/useCreditLimits';
import useBilling from '@/composables/useBilling';
import useRecordExportLimits from '@/composables/useRecordExportLimits';

import UsageLimitCallout, {
  type UsageLimitCalloutContent,
} from './UsageLimitCallout.vue';
import UsageSummaryCard from './UsageSummaryCard.vue';

const SEVENTY_FIVE_PERCENT = 0.75;

const {
  recordsExported,
  exportBaseLimit,
  exportExcess,
  hasExportExcess,
  limitUsedDecimal,
  percentOfAllowance,
  percentOfBase,
  limitUsedWidth,
} = useRecordExportLimits();

const { handleBillingInteraction } = useBilling();

const isRecordExportModalVisible = defineModel<boolean>();

function onContactUs() {
  handleBillingInteraction({
    cta: PLAN_AND_BILLING_CTAS.RECORD_EXPORT_LIMIT,
    talkToSalesReason: 'Record Export limit',
    event_site: EVENT_SITES.EXPORT_LIMIT_CALLOUT,
  });
}

/* Standardized 75 / 100 thresholds, matching the credit usage card. Scoped to
 * this billing card so the shared exportLimitStatus (integrations page,
 * notifications) keeps its existing 90% behavior. */
const statusColor = computed<UsageStatusColor>(() => {
  if (limitUsedDecimal.value >= 1) return 'danger';
  if (limitUsedDecimal.value >= SEVENTY_FIVE_PERCENT) return 'warning';
  return 'secondary';
});

const calloutContent = computed<UsageLimitCalloutContent | null>(() => {
  if (statusColor.value === 'warning') {
    return {
      title: `You've used ${percentOfAllowance.value} of your record export limit`,
      subtitle:
        'Integrations using record exports will be paused once the limit is hit, contact our team to discuss options',
      type: 'warning',
    };
  }
  if (statusColor.value === 'danger') {
    return {
      title: `You've used ${percentOfAllowance.value} of your record export limit`,
      subtitle:
        'Integrations using record exports have been paused, contact our team to discuss options',
      type: 'danger',
    };
  }
  return null;
});
</script>
